import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { 
  Building2, 
  Users, 
  FileText, 
  Calculator, 
  Plus,
  Search,
  Filter,
  BarChart3,
  TrendingUp,
  DollarSign,
  LogOut,
  Mail,
  CheckCircle
} from "lucide-react";

const CRM = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeQuotations: 0,
    pendingInvoices: 0,
    monthlyRevenue: 0
  });

  // Fetch clients data on component mount
  useEffect(() => {
    fetchClientsData();
  }, []);

  const fetchClientsData = async () => {
    try {
      setLoading(true);
      // Fetch clients from Supabase Edge Function
      const response = await fetch('https://axlxwpuaqgbfkoampsai.supabase.co/functions/v1/handle_client_applications_20251222_1300_v2', {
        headers: {
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
        
        // Calculate stats from real data
        const totalClients = data.clients?.length || 0;
        const companySetupClients = data.clients?.filter(c => c.application_type === 'company_setup').length || 0;
        const serviceRequestClients = data.clients?.filter(c => c.application_type === 'service_request').length || 0;
        const pendingClients = data.clients?.filter(c => c.status === 'pending').length || 0;
        
        setStats({
          totalClients,
          activeQuotations: companySetupClients,
          pendingInvoices: serviceRequestClients,
          monthlyRevenue: pendingClients
        });
      } else {
        // Fallback to demo data if backend not available
        console.log('Backend not available, using demo data');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('eliteedge_auth');
    localStorage.removeItem('eliteedge_user');
    localStorage.removeItem('eliteedge_login_time');
    
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    
    navigate("/");
  };
  const statsDisplay = [
    {
      icon: Users,
      title: "Total Clients",
      value: loading ? "..." : stats.totalClients.toString(),
      change: "+New",
      color: "text-blue-600"
    },
    {
      icon: FileText,
      title: "Company Setup Applications",
      value: loading ? "..." : stats.activeQuotations.toString(),
      change: "+Active",
      color: "text-green-600"
    },
    {
      icon: Calculator,
      title: "Service Requests",
      value: loading ? "..." : stats.pendingInvoices.toString(),
      change: "+Requests",
      color: "text-orange-600"
    },
    {
      icon: DollarSign,
      title: "Pending Applications",
      value: loading ? "..." : stats.monthlyRevenue.toString(),
      change: "+Pending",
      color: "text-purple-600"
    }
  ];

  const quickActions = [
    {
      icon: Plus,
      title: "Add New Client",
      description: "Create and manage client records with unique IDs",
      link: "/client-management",
      color: "bg-blue-500"
    },
    {
      icon: FileText,
      title: "Create Quotation",
      description: "Generate a new quotation for services",
      link: "/quotation",
      color: "bg-green-500"
    },
    {
      icon: Calculator,
      title: "Generate Invoice",
      description: "Create and send professional invoices",
      link: "/invoices",
      color: "bg-purple-500"
    },
    {
      icon: Users,
      title: "Client Database",
      description: "View and manage existing client database",
      link: "/client-management",
      color: "bg-orange-500"
    },
    {
      icon: FileText,
      title: "Letter of Engagement",
      description: "Create and manage engagement letters",
      link: "/letter-of-engagement",
      color: "bg-indigo-500"
    }
  ];

  // Generate recent activities from real client data
  const recentActivities = clients.slice(0, 5).map((client, index) => {
    const timeAgo = new Date(client.created_at).toLocaleDateString();
    const applicationType = client.application_type === 'company_setup' ? 'Company Setup' : 'Service Request';
    
    return {
      type: client.application_type,
      message: `New ${applicationType} application from '${client.full_name}'${client.company_name ? ` (${client.company_name})` : ''}`,
      time: timeAgo,
      status: client.status === 'pending' ? 'pending' : 'success'
    };
  });

  // Fallback activities if no real data
  const fallbackActivities = [
    {
      type: "info",
      message: "CRM system ready - waiting for client applications",
      time: "Now",
      status: "info"
    },
    {
      type: "system",
      message: "Backend integration configured",
      time: "Today",
      status: "success"
    }
  ];

  const displayActivities = recentActivities.length > 0 ? recentActivities : fallbackActivities;

  // Handler functions for client actions
  const handleContactClient = (client) => {
    const subject = `Regarding your ${client.application_type === 'company_setup' ? 'Company Setup' : 'Service Request'} application`;
    const body = `Dear ${client.full_name},\n\nThank you for your application. We would like to discuss your requirements further.\n\nBest regards,\nElite Edge Management Consultancy`;
    window.open(`mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const handleCreateQuotation = async (client) => {
    try {
      const quotationData = {
        client_id: client.id,
        client_name: client.full_name,
        client_email: client.email,
        client_phone: client.phone,
        client_company: client.company_name,
        title: `${client.application_type === 'company_setup' ? 'Company Setup Services' : 'Professional Services'} - ${client.full_name}`,
        description: client.project_description || `Services for ${client.application_type === 'company_setup' ? 'company setup' : 'service request'}`,
        services: [
          {
            description: client.application_type === 'company_setup' 
              ? `Trade License Registration - ${client.business_activity || 'General'}` 
              : `${client.service_type || 'Professional Services'}`,
            quantity: 1,
            rate: client.application_type === 'company_setup' ? 2500 : 1500,
            amount: client.application_type === 'company_setup' ? 2500 : 1500
          }
        ],
        terms_conditions: 'Payment terms: 50% advance, 50% upon completion\nValidity: 30 days from quotation date\nPrices are inclusive of VAT',
        valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      };

      const response = await fetch('https://axlxwpuaqgbfkoampsai.supabase.co/functions/v1/handle_quotations_20251222_1300', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
        },
        body: JSON.stringify(quotationData)
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Quotation Created!",
          description: `Quotation ${result.quotation_number} has been created and sent to ${client.email}`,
        });
      } else {
        throw new Error('Failed to create quotation');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create quotation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCreateInvoice = async (client) => {
    try {
      const invoiceData = {
        client_id: client.id,
        client_name: client.full_name,
        client_email: client.email,
        client_phone: client.phone,
        client_company: client.company_name,
        title: `Invoice for ${client.application_type === 'company_setup' ? 'Company Setup Services' : 'Professional Services'} - ${client.full_name}`,
        description: client.project_description || `Services for ${client.application_type === 'company_setup' ? 'company setup' : 'service request'}`,
        services: [
          {
            description: client.application_type === 'company_setup' 
              ? `Trade License Registration - ${client.business_activity || 'General'}` 
              : `${client.service_type || 'Professional Services'}`,
            quantity: 1,
            rate: client.application_type === 'company_setup' ? 2500 : 1500,
            amount: client.application_type === 'company_setup' ? 2500 : 1500
          }
        ],
        payment_terms: 'Net 30 days'
      };

      const response = await fetch('https://axlxwpuaqgbfkoampsai.supabase.co/functions/v1/handle_invoices_20251222_1300', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
        },
        body: JSON.stringify(invoiceData)
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Invoice Created!",
          description: `Invoice ${result.invoice_number} has been created and sent to ${client.email}`,
        });
      } else {
        throw new Error('Failed to create invoice');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create invoice. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMarkComplete = async (client) => {
    try {
      // Update client status to completed
      const response = await fetch('https://axlxwpuaqgbfkoampsai.supabase.co/functions/v1/handle_client_applications_20251222_1300_v2', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
        },
        body: JSON.stringify({ client_id: client.id, status: 'completed' })
      });

      if (response.ok) {
        toast({
          title: "Status Updated!",
          description: `${client.full_name}'s application has been marked as completed.`,
        });
        // Refresh the client data
        fetchClientsData();
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update client status. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading CRM Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-3xl font-bold text-primary">Elite Edge Management Consultancy</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <span className="text-sm text-muted-foreground">Welcome, Admin</span>
              <Button asChild>
                <Link to="/client-form">Add Client</Link>
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-8 px-4 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">CRM Dashboard</h1>
              <p className="text-xl text-muted-foreground">
                Manage clients, quotations, and invoices efficiently
              </p>
            </div>
            <Badge variant="secondary" className="text-lg px-4 py-2">
              Business Consultancy CRM
            </Badge>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-8 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsDisplay.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                        <p className="text-3xl font-bold">{stat.value}</p>
                        <p className={`text-sm ${stat.color}`}>{stat.change} from last month</p>
                      </div>
                      <div className={`p-3 rounded-full bg-primary/10`}>
                        <Icon className={`h-6 w-6 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-8 px-4">
        <div className="container mx-auto">
          <h2 className="text-2xl font-bold mb-6">Quick Actions</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <Link to={action.link}>
                    <CardHeader className="text-center">
                      <div className={`w-16 h-16 ${action.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-lg">{action.title}</CardTitle>
                      <CardDescription>{action.description}</CardDescription>
                    </CardHeader>
                  </Link>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-8 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Recent Activities */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Recent Activities</CardTitle>
                    <Button variant="outline" size="sm">
                      <Filter className="h-4 w-4 mr-2" />
                      Filter
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {displayActivities.map((activity, index) => (
                      <div key={index} className="flex items-center space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className={`w-3 h-3 rounded-full ${
                          activity.status === 'success' ? 'bg-green-500' :
                          activity.status === 'pending' ? 'bg-yellow-500' :
                          activity.status === 'info' ? 'bg-blue-500' : 'bg-gray-500'
                        }`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{activity.message}</p>
                          <p className="text-xs text-muted-foreground">{activity.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" className="w-full mt-4">
                    View All Activities
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    Performance Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Client Satisfaction</span>
                      <span className="font-semibold text-green-600">98%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Quote Conversion</span>
                      <span className="font-semibold text-blue-600">75%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Payment Collection</span>
                      <span className="font-semibold text-purple-600">92%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">License Renewals</span>
                      <span className="font-semibold text-orange-600">15 Due</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Monthly Targets
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>New Clients</span>
                        <span>24/30</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{width: '80%'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Revenue Target</span>
                        <span>125K/150K</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full" style={{width: '83%'}}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Quotations</span>
                        <span>34/40</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-purple-600 h-2 rounded-full" style={{width: '85%'}}></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Navigation */}
      <section className="py-8 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Search className="h-5 w-5 mr-2" />
                  Quick Search
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Find clients, quotations, or invoices quickly
                </p>
                <Button className="w-full" variant="outline">
                  Open Search
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Generate detailed business reports and analytics
                </p>
                <Button className="w-full" variant="outline">
                  View Reports
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="h-5 w-5 mr-2" />
                  Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Configure CRM settings and preferences
                </p>
                <Button className="w-full" variant="outline">
                  Open Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Detailed Client Applications */}
      <section className="py-8 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-2">Client Applications</h2>
            <p className="text-muted-foreground">Detailed view of all submitted applications</p>
          </div>
          
          <div className="grid gap-6">
            {clients.length > 0 ? (
              clients.map((client, index) => (
                <Card key={client.id || index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{client.full_name}</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {client.application_type === 'company_setup' ? 'Company Setup Application' : 'Service Request'}
                          {client.company_name && ` • ${client.company_name}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          client.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          client.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          client.status === 'completed' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {client.status?.charAt(0).toUpperCase() + client.status?.slice(1) || 'Pending'}
                        </span>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(client.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold mb-2">Contact Information</h4>
                        <div className="space-y-1 text-sm">
                          <p><strong>Email:</strong> {client.email}</p>
                          {client.phone && <p><strong>Phone:</strong> {client.phone}</p>}
                          {client.contact_city && <p><strong>Location:</strong> {client.contact_city}, {client.contact_country || 'UAE'}</p>}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold mb-2">Application Details</h4>
                        <div className="space-y-1 text-sm">
                          {client.application_type === 'company_setup' ? (
                            <>
                              {client.jurisdiction_preference && <p><strong>Jurisdiction:</strong> {client.jurisdiction_preference}</p>}
                              {client.business_activity && <p><strong>Business Activity:</strong> {client.business_activity}</p>}
                              {client.company_structure && <p><strong>Structure:</strong> {client.company_structure}</p>}
                              {client.office_requirement && <p><strong>Office:</strong> {client.office_requirement}</p>}
                            </>
                          ) : (
                            <>
                              {client.service_type && <p><strong>Service:</strong> {client.service_type}</p>}
                              {client.timeline && <p><strong>Timeline:</strong> {client.timeline}</p>}
                              {client.budget_range && <p><strong>Budget:</strong> {client.budget_range}</p>}
                              {client.project_description && (
                                <div>
                                  <strong>Description:</strong>
                                  <p className="mt-1 p-2 bg-gray-50 rounded text-xs">{client.project_description}</p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleContactClient(client)}
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        Contact
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleCreateQuotation(client)}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Create Quote
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleCreateInvoice(client)}
                      >
                        <Calculator className="h-4 w-4 mr-1" />
                        Create Invoice
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleMarkComplete(client)}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Mark Complete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="text-center py-8">
                  <p className="text-muted-foreground">No applications submitted yet.</p>
                  <p className="text-sm text-muted-foreground mt-2">Applications will appear here once clients submit forms.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CRM;